import type { RoundDuration } from '@hooks/useSettings';
import { getCurrentTime } from './timeshiftService';

/**
 * Get next midnight PST/PDT as UTC timestamp
 */
function getNextMidnightPST(): number {
  const now = new Date(getCurrentTime());
  
  // Get current time components in PST
  const pstFormatter = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/Los_Angeles',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
  
  const parts = pstFormatter.formatToParts(now);
  const pstYear = parseInt(parts.find(p => p.type === 'year')!.value);
  const pstMonth = parseInt(parts.find(p => p.type === 'month')!.value) - 1; // 0-indexed
  const pstDay = parseInt(parts.find(p => p.type === 'day')!.value);
  const pstHour = parseInt(parts.find(p => p.type === 'hour')!.value);
  
  // Determine target day (tomorrow if we've passed midnight PST today)
  const targetDay = pstHour >= 0 ? pstDay + 1 : pstDay;
  
  // Try different UTC times to find which one corresponds to midnight PST
  // PST is UTC-8, PDT is UTC-7
  for (let utcHour = 7; utcHour <= 8; utcHour++) {
    const testUtc = new Date(Date.UTC(pstYear, pstMonth, targetDay, utcHour, 0, 0, 0));
    const testParts = pstFormatter.formatToParts(testUtc);
    const testHour = parseInt(testParts.find(p => p.type === 'hour')!.value);
    const testDay = parseInt(testParts.find(p => p.type === 'day')!.value);
    
    if (testHour === 0 && testDay === targetDay) {
      return testUtc.getTime();
    }
  }
  
  // Fallback: use 8am UTC (PST, UTC-8)
  return new Date(Date.UTC(pstYear, pstMonth, targetDay, 8, 0, 0, 0)).getTime();
}

export interface RoundInfo {
  currentRoundExpiry: number; // timestamp in ms
  timeToExpiryMs: number;
  timeToExpiryHours: number;
  isExpired: boolean;
}

export interface TimeLeft {
  hours: number;
  minutes: number;
  seconds: number;
}

/**
 * Centralized service for managing option round timing.
 * Options expire based on the configured round duration.
 */
export class RoundService {
  /**
   * Convert duration string to milliseconds
   */
  static durationToMs(duration: RoundDuration): number {
    const minute = 60 * 1000;
    const hour = 60 * minute;
    const day = 24 * hour;

    switch (duration) {
      case '1m':
        return minute;
      case '5m':
        return 5 * minute;
      case '15m':
        return 15 * minute;
      case '30m':
        return 30 * minute;
      case '1h':
        return hour;
      case '2h':
        return 2 * hour;
      case '4h':
        return 4 * hour;
      case '8h':
        return 8 * hour;
      case '12h':
        return 12 * hour;
      case '1d':
        return day;
      default:
        return day;
    }
  }

  /**
   * Get the configured round duration from localStorage, defaulting to 1d (24 hours)
   * On reload, this resets to '1d' to represent the default 16+24 hour cycle.
   */
  static getRoundDuration(): RoundDuration {
    try {
      const stored = localStorage.getItem('yv_round_duration_v1');
      return stored ? JSON.parse(stored) : '1d';
    } catch {
      return '1d';
    }
  }

  /**
   * Get the round start timestamp based on current expiry and duration
   * Default behavior: 16 hours ago rounded to the nearest hour (for 24h duration)
   * During session: respects the round duration setting
   */
  static getRoundStartTimestamp(): number {
    const duration = this.getRoundDuration();
    const currentExpiry = this.getCurrentRoundExpiry();
    const durationMs = this.durationToMs(duration);
    
    // If duration is 24h (1d), use fixed 16-hour lookback rounded to nearest hour
    if (duration === '1d') {
      const now = getCurrentTime();
      const hourMs = 60 * 60 * 1000;
      const sixteenHoursMs = 16 * hourMs;
      const sixteenHoursAgo = now - sixteenHoursMs;
      return Math.round(sixteenHoursAgo / hourMs) * hourMs;
    }
    
    // For other durations, calculate start from expiry
    return currentExpiry - durationMs;
  }

  /**
   * Get the current round's expiry timestamp based on configured duration
   * Default behavior: 24 hours from round start (for 16+24 hour cycle)
   * During session: respects the round duration setting
   */
  static getCurrentRoundExpiry(): number {
    const duration = this.getRoundDuration();
    const durationMs = this.durationToMs(duration);
    const now = getCurrentTime();

    if (duration === '1d') {
      // Default: 24 hours from round start (which is 16 hours ago)
      // So expiry is 8 hours from now, rounded to nearest hour
      const hourMs = 60 * 60 * 1000;
      const eightHoursMs = 8 * hourMs;
      const eightHoursFromNow = now + eightHoursMs;
      return Math.round(eightHoursFromNow / hourMs) * hourMs;
    } else {
      // For other durations: find next interval boundary
      const intervalStart = Math.floor(now / durationMs) * durationMs;
      return intervalStart + durationMs;
    }
  }

  /**
   * Get complete information about the current round
   */
  static getCurrentRoundInfo(): RoundInfo {
    const now = getCurrentTime();
    const currentRoundExpiry = this.getCurrentRoundExpiry();
    const timeToExpiryMs = Math.max(0, currentRoundExpiry - now);
    const timeToExpiryHours = timeToExpiryMs / (1000 * 60 * 60);
    const isExpired = timeToExpiryMs <= 0;

    return {
      currentRoundExpiry,
      timeToExpiryMs,
      timeToExpiryHours,
      isExpired,
    };
  }

  /**
   * Get time left in current round as hours/minutes/seconds object
   */
  static getTimeLeftInCurrentRound(): TimeLeft {
    const { timeToExpiryMs } = this.getCurrentRoundInfo();
    const totalSeconds = Math.floor(timeToExpiryMs / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    return { hours, minutes, seconds };
  }

  /**
   * Format time left as HH:MM:SS string
   */
  static formatTimeLeft(timeLeft?: TimeLeft): string {
    timeLeft ??= this.getTimeLeftInCurrentRound();
    const h = timeLeft.hours.toString().padStart(2, '0');
    const m = timeLeft.minutes.toString().padStart(2, '0');
    const s = timeLeft.seconds.toString().padStart(2, '0');
    return `${h}:${m}:${s}`;
  }

  /**
   * Check if a specific timestamp is in the current round
   */
  static isInCurrentRound(timestamp: number): boolean {
    const roundStart = this.getRoundStartTimestamp();
    const { currentRoundExpiry } = this.getCurrentRoundInfo();
    
    return timestamp > roundStart && timestamp <= currentRoundExpiry;
  }
}
